import React, { useCallback, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { SiteConfigManager, useLabel } from '@atlas/core';
import { Button, Icon, Label } from '@atlas/ui';

import { bonusStatuses } from 'common/constants';
import ConfirmPopup from 'common/Popup/ConfirmPopup';
import { capitalize } from 'common/utils/capitalize';
import BonusStatus from 'components/ClientProfile/Bonuses/DepositBonuses/BunusesItemExpand/BonusStatus';
import BonusTableListItemExpandDescription from 'components/ClientProfile/Bonuses/DepositBonuses/BunusesItemExpand/BonusTableListItemExpandDescription';
import BonusTableListItemExpandInfo from 'components/ClientProfile/Bonuses/DepositBonuses/BunusesItemExpand/BonusTableListItemExpandInfo';

const { ACTIVATED, IN_PROGRESS } = bonusStatuses;

const BonusListItem = ({ activateBonus, bonus, deactivateBonus, wallet }) => {
  const { name, productType, requiredBetsToWager, status, wageredBets } = bonus;
  const hideBonusCancelFromConfig = SiteConfigManager.getConfig(
    'config.config.general.hideBonusCancel',
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoadingDeactivation, setIsLoadingDeactivation] = useState(false);

  const toggleIsExpanded = useCallback(() => {
    setIsExpanded(isExpanded => !isExpanded);
  }, [setIsExpanded]);

  const [isExpandedPopup, setIsExpandedPopup] = useState(false);

  const toggleExpandPopup = useCallback(() => {
    setIsExpandedPopup(isExpanded => !isExpanded);
  }, [setIsExpandedPopup]);

  const deactivateBonusHandler = useCallback(async () => {
    setIsLoadingDeactivation(true);
    await deactivateBonus(bonus).then(() => {
      setIsLoadingDeactivation(false);
    });
  }, [bonus, deactivateBonus]);

  const bonusClassName = cx('bonus-history-list-item', {
    sportsbook: productType === 'SPORTSBOOK',
  });

  const bonusInfoClassName = cx('bonus-info-container', { active: isExpanded });

  const bonusTypeTranslation = {
    CASINO: 'profile_bonuses_casino',
    SPORTSBOOK: 'profile_bonuses_sportsbook',
  };

  const label = useLabel(bonusTypeTranslation[productType]);

  return (
    <div className={bonusClassName}>
      <div className={bonusInfoClassName} onClick={toggleIsExpanded}>
        <div className="bonus-info">
          <span className="title-text">{capitalize(name)} </span>
          <span className="product-label">
            <span className="sub-title-text">
              <Label message="Product" />:
            </span>
            <span className="title-small-text">{capitalize(label)} </span>
          </span>
        </div>
        <div className="arrow-container">
          <Icon className="icon-arrow-down" name="arrow-down" />
        </div>
      </div>
      <div className="bonus-status-container">
        <BonusStatus
          activateBonus={activateBonus}
          bonus={bonus}
          deactivateBonus={deactivateBonus}
        />
      </div>
      {isExpanded && (
        <div className="bonus-info-expanded">
          <BonusTableListItemExpandInfo bonus={bonus} wallet={wallet} />
          {requiredBetsToWager && (
            <div className="product-label big-margin">
              <span className="sub-title-text">
                <Label message="wagered_bets" />:
                <span className="title-small-text">
                  {wageredBets} <Label message="bonuses_bets_from" /> {requiredBetsToWager}
                </span>
              </span>
            </div>
          )}
          <BonusTableListItemExpandDescription bonus={bonus} />
          {!hideBonusCancelFromConfig && (status === IN_PROGRESS || status === ACTIVATED) && (
            <>
              <div className="activated-container">
                <Button
                  className="block"
                  label="cancel bonus"
                  onClick={toggleExpandPopup}
                  size="medium"
                  type="secondary"
                />
              </div>
              {isExpandedPopup && (
                <ConfirmPopup
                  className="bonus-deactivated-popup"
                  data={{
                    closeLabel: 'close',
                    confirmLabel: 'yes_cancel',
                    onClose: toggleExpandPopup,
                    onConfirm: deactivateBonusHandler,
                  }}
                  isDisabledCloseButton={isLoadingDeactivation}
                  isDisabledConfirmButton={isLoadingDeactivation}
                  isLoadingCloseButton={isLoadingDeactivation}
                  isLoadingConfirmButton={isLoadingDeactivation}
                  message="cancel_bonus_text"
                  title="cancel_bonus_title"
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

BonusListItem.propTypes = {
  activateBonus: PropTypes.func.isRequired,
  bonus: PropTypes.object.isRequired,
  deactivateBonus: PropTypes.func.isRequired,
  wallet: PropTypes.object.isRequired,
};

export default BonusListItem;
